import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, MaskLines, MaskLinesInView } from "@/components/Reveal";
import BrandMarquee from "@/components/BrandMarquee";
import CTASection from "@/components/CTASection";
import { SERVICES, COVEY } from "@/lib/data";
import eseSmall from "@/assets/brand/ese_light_logo.webp";

function ServiceCard({ s, index, ese = false }) {
  return (
    <Reveal delay={index * 0.06}>
      <Link
        to={s.slug === "exit-on-your-terms" ? "/services/exit-and-sell" : `/services/${s.slug}`}
        data-testid={`service-card-${s.slug}`}
        className={`group relative flex flex-col justify-between h-full p-8 md:p-10 border transition-colors duration-500 ${
          ese
            ? "border-amber/30 bg-blue-deep hover:bg-blue text-cream"
            : "border-blue/12 bg-cream hover:bg-blue text-blue"
        }`}
      >
        <div>
          <div className="flex items-center justify-between mb-8">
            <span
              className={`eyebrow text-[0.6rem] ${
                ese ? "text-amber-bright" : "text-amber-deep group-hover:text-amber-bright"
              } transition-colors`}
            >
              {s.stage}
            </span>
            <ArrowUpRight
              size={22}
              className={`${
                ese ? "text-amber-bright" : "text-blue/40 group-hover:text-amber-bright"
              } group-hover:rotate-45 transition-all duration-400`}
            />
          </div>
          <h3
            className={`font-serif font-bold text-3xl md:text-4xl leading-tight ${
              ese ? "text-cream" : "group-hover:text-cream"
            } transition-colors`}
          >
            {s.title}{" "}
            <span className="italic text-amber-deep group-hover:text-amber-bright transition-colors">
              {s.titleItalic}
            </span>
          </h3>
          <p
            className={`font-body mt-5 leading-relaxed ${
              ese ? "text-cream/75" : "text-charcoal/70 group-hover:text-cream/75"
            } transition-colors`}
          >
            {s.tagline}
          </p>
        </div>
        <div
          className={`mt-8 pt-5 border-t flex items-center justify-between ${
            ese ? "border-cream/15" : "border-blue/10 group-hover:border-cream/15"
          }`}
        >
          <span
            className={`eyebrow text-[0.58rem] ${
              ese ? "text-amber-bright" : "text-blue/50 group-hover:text-amber-bright"
            } transition-colors`}
          >
            {s.savings}
          </span>
          <span
            className={`font-body text-xs ${
              ese ? "text-cream/50" : "text-slate group-hover:text-cream/50"
            } transition-colors`}
          >
            {s.timeline.split("·")[0]}
          </span>
        </div>
      </Link>
    </Reveal>
  );
}

export default function Services() {
  const lhg = SERVICES.filter((s) => s.brand === "lhg");
  const ese = SERVICES.filter((s) => s.brand === "ese");

  return (
    <>
      <section className="bg-cream grain pt-36 pb-20 md:pt-48 md:pb-28 relative">
        <div className="container-editorial relative z-10 max-w-4xl">
          <Reveal>
            <p className="eyebrow text-amber-deep mb-8 flex items-center gap-3">
              <span className="w-10 h-px bg-amber-deep" /> The Services
            </p>
          </Reveal>
          <h1 className="font-serif font-bold text-5xl md:text-7xl lg:text-8xl text-blue leading-[0.98] tracking-tight">
            <MaskLines lines={["The whole journey,"]} delay={0.1} />
            <MaskLines lines={["from Start-Up to Exiting on Top."]} className="italic text-amber-deep" delay={0.26} />
          </h1>
        </div>
      </section>

      <BrandMarquee />

      {/* LHG Journey */}
      <section data-testid="lhg-services" className="bg-cream py-24 md:py-32">
        <div className="container-editorial">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <p className="eyebrow text-amber-deep mb-4">The Journey</p>
              <h2 className="font-serif font-bold text-4xl md:text-5xl text-blue">The LHG Journey</h2>
            </div>
            <p className="font-body text-slate max-w-md text-sm md:text-right">
              Launch · Grow &amp; Expand · Government Contracting · SBA Pre-Packaging · Risk Management
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {lhg.map((s, i) => (
              <ServiceCard key={s.slug} s={s} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ESE Destination, flagship */}
      <section data-testid="ese-services" className="relative bg-blue-deep text-cream py-24 md:py-32 grain overflow-hidden">
        <div className="container-editorial relative z-10">
          <div className="flex items-center gap-6 mb-8 flex-wrap">
            <div className="bg-cream p-3 md:p-4 shrink-0">
              <img src={eseSmall} alt="Exit Strategy Experts" className="h-14 md:h-16 w-auto" />
            </div>
            <div>
              <p className="eyebrow text-amber-bright mb-3">The Destination · Flagship</p>
              <h2 className="font-serif font-bold text-4xl md:text-5xl text-cream">
                Exit Strategy <span className="italic text-amber-bright">Experts</span>
              </h2>
            </div>
          </div>
          <Reveal>
            <p className="font-serif italic text-amber-bright/90 text-lg mb-2">
              &ldquo;{COVEY.line}&rdquo;
            </p>
            <p className="eyebrow text-[0.58rem] text-cream/45 mb-10">{COVEY.attribution}</p>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            {ese.map((s, i) => (
              <ServiceCard key={s.slug} s={s} index={i} ese />
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
